import React, { useState, useEffect } from 'react';
import AdminHome from './AdminHome';
import { IoCloseSharp } from "react-icons/io5";

const AdminStylesList = () => {
  const [styles, setStyles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editForm, setEditForm] = useState({
    categoryType: '',
    styleName: '',
    selectedCategories: []
  });

  useEffect(() => {
    fetchStyles();
    fetchCategories();
  }, []);

  const fetchStyles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/styles-categories');
      const data = await response.json();
      console.log('Fetched styles data:', data);
      
      if (Array.isArray(data)) {
        setStyles(data);
      } else if (data.styles && Array.isArray(data.styles)) {
        setStyles(data.styles);
      } else if (data && typeof data === 'object') {
        // If single object is returned, wrap it in an array
        setStyles([data]);
      } else {
        setStyles([]);
        console.error('Invalid data format received:', data);
      }
    } catch (error) {
      console.error('Error fetching styles:', error);
      setError('Failed to load styles');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      console.log('Fetched categories data:', data);
      
      if (Array.isArray(data)) {
        setCategories(data);
      } else if (data.categories && Array.isArray(data.categories)) {
        setCategories(data.categories);
      } else {
        setCategories([]);
        console.error('Invalid categories data format:', data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this style?')) {
      try {
        const response = await fetch(`/api/styles-categories/${id}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          fetchStyles();
          alert('Style deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting style:', error);
        alert('Error deleting style');
      }
    }
  };

  const handleEdit = (style) => {
    console.log('Editing style:', style);
    setSelectedStyle(style);
    setEditForm({
      categoryType: style.categoryType,
      styleName: style.styleName,
      selectedCategories: style.selectedCategories || []
    });
    setShowEditModal(true);
  };

  const handleCategoryToggle = (category) => {
    console.log('Toggling category:', category);
    setEditForm(prev => {
      const categoryExists = prev.selectedCategories.some(
        item => item.categoryName === category.categoryName
      );
      
      if (categoryExists) {
        return {
          ...prev,
          selectedCategories: prev.selectedCategories.filter(
            item => item.categoryName !== category.categoryName
          )
        };
      } else {
        return {
          ...prev,
          selectedCategories: [...prev.selectedCategories, {
            categoryId: category.categoryId,
            categoryName: category.categoryName
          }]
        };
      }
    });
  };

  const handleUpdate = async () => {
    try {
      console.log('Updating style with data:', editForm);
      const response = await fetch(`/api/styles-categories/${selectedStyle._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        await fetchStyles();
        setShowEditModal(false);
        alert('Style updated successfully!');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update style');
      }
    } catch (error) {
      console.error('Error updating style:', error);
      alert(error.message || 'Error updating style');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="row">
          <AdminHome />
          <div className="col-lg-9">
            <div className="loading">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="row">
          <AdminHome />
          <div className="col-lg-9">
            <div className="error">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="tf-page-title">
        <div className="container-full">
          <div className="heading text-center">Manage Styles and Categories</div>
        </div>
      </div>
      
      <section className="flat-spacing-11">
        <div className="container">
          <div className="row">
            <AdminHome />
            <div className="col-lg-9">
              <div className="styles-list">
                {styles.length === 0 ? (
                  <div className="no-styles">No styles found</div>
                ) : (
                  styles.map((style) => (
                    <div key={style._id} className="style-card">
                      <div className="style-header">
                        <h3>{style.styleName}</h3>
                        <div className="style-type">Type: {style.categoryType}</div>
                      </div>
                      <div className="categories-list">
                        {(style.selectedCategories || []).map((category) => (
                          <span key={category._id || category.categoryId} className="category-tag">
                            {category.categoryName}
                          </span>
                        ))}
                      </div>
                      <div className="action-buttons">
                        <button 
                          onClick={() => handleEdit(style)}
                          className="edit-btn"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(style._id)}
                          className="delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Style</h2>
              <button 
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                <IoCloseSharp />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Category Type:</label>
                <input
                  type="text"
                  value={editForm.categoryType}
                  onChange={(e) => setEditForm({ ...editForm, categoryType: e.target.value })}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Style Name:</label>
                <input
                  type="text"
                  value={editForm.styleName}
                  onChange={(e) => setEditForm({ ...editForm, styleName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Categories:</label>
                <div className="checkbox-grid">
                  {categories.map((category) => {
                    const isChecked = editForm.selectedCategories.some(
                      selectedCat => selectedCat.categoryName === category.categoryName
                    );
                    
                    return (
                      <div key={category.categoryId} className="checkbox-item">
                        <input
                          type="checkbox"
                          id={`edit-category-${category.categoryId}`}
                          checked={isChecked}
                          onChange={() => handleCategoryToggle(category)}
                        />
                        <label htmlFor={`edit-category-${category.categoryId}`}>
                          {category.categoryName}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="update-btn" onClick={handleUpdate}>Update</button>
              <button className="cancel-btn" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .styles-list {
          display: grid;
          gap: 20px;
          padding: 20px;
        }

        .style-card {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .loading {
          text-align: center;
          padding: 20px;
          font-size: 18px;
        }

        .error {
          color: #dc3545;
          text-align: center;
          padding: 20px;
          font-size: 18px;
        }

        .no-styles {
          text-align: center;
          padding: 20px;
          color: #666;
          font-style: italic;
        }

        .style-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .style-header h3 {
          margin: 0;
          color: #333;
        }

        .style-type {
          color: #666;
          font-size: 0.9em;
        }

        .categories-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 15px;
        }

        .category-tag {
          background: rgb(29, 158, 31);
          color: white;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 14px;
        }

        .action-buttons {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .edit-btn, .update-btn {
          background: rgb(30, 93, 36);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .edit-btn:hover, .update-btn:hover {
          background: rgb(25, 78, 30);
        }

        .delete-btn {
          background: #dc3545;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .delete-btn:hover {
          background: #c82333;
        }

        .cancel-btn {
          background: #6c757d;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .cancel-btn:hover {
          background: #5a6268;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          padding: 15px 20px;
          border-bottom: 1px solid #ddd;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          padding: 15px 20px;
          border-top: 1px solid #ddd;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #666;
          transition: color 0.2s;
        }

        .close-btn:hover {
          color: #333;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #333;
          font-weight: 500;
        }

        .checkbox-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 12px;
          margin-top: 10px;
        }

        .checkbox-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .checkbox-item label {
          margin: 0;
          cursor: pointer;
        }

        input[type="text"] {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        input[type="text"]:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        input[type="checkbox"] {
          cursor: pointer;
        }
      `}</style>
    </>
  );
};

export default AdminStylesList;